<template>
  <div>
    <!-- 1. Básico -->
    <vx-card class="mb-4">
      <h4 class="mb-1">{{ $t('campaign.section_basic') }}</h4>
      <div class="vx-row">
        <div class="vx-col sm:w-5/12 w-full">
          <b-form-group :label="$t('campaign.name')" label-for="name">
            <b-form-input id="name" v-model="model.name" type="text" class="required"/>
          </b-form-group>
        </div>
        <div class="vx-col sm:w-3/12 w-full">
          <b-form-group :label="$t('campaign.legal_entity')" label-for="legal_entity_id">
            <v-select
              id="legal_entity_id"
              v-model="model.legal_entity_id"
              :options="legalEntities"
              :reduce="opt => opt.id"
              label="name"
              class="w-full required-select"/>
          </b-form-group>
        </div>
        <div class="vx-col sm:w-2/12 w-full">
          <b-form-group :label="$t('campaign.starts_at')" label-for="starts_at">
            <b-form-input id="starts_at" v-model="model.starts_at" type="date"/>
          </b-form-group>
        </div>
        <div class="vx-col sm:w-2/12 w-full">
          <b-form-group :label="$t('campaign.ends_at')" label-for="ends_at">
            <b-form-input id="ends_at" v-model="model.ends_at" type="date"/>
          </b-form-group>
        </div>
      </div>
      <b-form-group :label="$t('campaign.objective')" label-for="objective">
        <b-form-input id="objective" v-model="model.objective" type="text"/>
      </b-form-group>
    </vx-card>

    <!-- 2. Produtos -->
    <vx-card class="mb-4">
      <h4 class="mb-1">{{ $t('campaign.section_products') }}</h4>
      <small class="text-muted d-block mb-2">{{ $t('campaign.section_products_hint') }}</small>
      <v-select
        v-model="model.product_ids"
        :options="products"
        :reduce="opt => opt.id"
        label="name"
        multiple
        class="w-full">
        <div slot="no-options">{{ $t('campaign.no_products') }}</div>
      </v-select>
    </vx-card>

    <!-- 3. Desconto, comissão e simulador -->
    <vx-card class="mb-4">
      <h4 class="mb-1">{{ $t('campaign.section_money') }}</h4>
      <div class="vx-row">
        <div class="vx-col sm:w-3/12 w-full">
          <b-form-group :label="$t('campaign.discount_type')">
            <b-form-radio-group v-model="model.discount_type" :options="typeList"/>
          </b-form-group>
        </div>
        <div class="vx-col sm:w-3/12 w-full">
          <b-form-group :label="$t('campaign.discount')">
            <b-input-group :prepend="model.discount_type === 'VA' ? 'R$' : '%'">
              <b-form-input v-model="model.discount_amount" type="number" step="0.01" min="0"/>
            </b-input-group>
          </b-form-group>
        </div>
        <div class="vx-col sm:w-3/12 w-full">
          <b-form-group :label="$t('campaign.commission_type')">
            <b-form-radio-group v-model="model.commission_type" :options="typeList"/>
          </b-form-group>
        </div>
        <div class="vx-col sm:w-3/12 w-full">
          <b-form-group :label="$t('campaign.commission')">
            <b-input-group :prepend="model.commission_type === 'VA' ? 'R$' : '%'">
              <b-form-input v-model="model.commission_amount" type="number" step="0.01" min="0"/>
            </b-input-group>
          </b-form-group>
        </div>
      </div>

      <div class="vx-row">
        <div class="vx-col sm:w-4/12 w-full">
          <b-form-group :label="$t('campaign.commission_base')">
            <b-form-radio-group v-model="model.commission_base" :options="baseList"/>
            <small class="text-muted">{{ $t('campaign.commission_base_hint') }}</small>
          </b-form-group>
        </div>
        <div class="vx-col sm:w-4/12 w-full">
          <b-form-group :label="$t('campaign.cost_bearer')">
            <b-form-select v-model="model.cost_bearer" :options="bearerList"/>
          </b-form-group>
        </div>
        <div class="vx-col sm:w-4/12 w-full">
          <b-form-group :label="$t('campaign.budget_cap')">
            <b-input-group prepend="R$">
              <b-form-input v-model="model.budget_cap" type="number" step="0.01" min="0"/>
            </b-input-group>
            <small class="text-muted">{{ $t('campaign.budget_cap_hint') }}</small>
          </b-form-group>
        </div>
      </div>

      <!-- Simulador de margem: impede publicar campanha que dá prejuízo antes de ela existir -->
      <div class="simulator">
        <div class="flex items-center justify-between mb-1">
          <h5 class="mb-0">{{ $t('campaign.simulator') }}</h5>
          <div class="flex items-center gap-1">
            <small class="text-muted">{{ $t('campaign.simulator_price') }}</small>
            <b-form-input v-model="simPrice" type="number" step="0.01" min="0" size="sm" style="width: 110px"/>
            <small class="text-muted ml-1">{{ $t('campaign.simulator_fee') }}</small>
            <b-form-input v-model="simFeePct" type="number" step="0.1" min="0" max="100" size="sm" style="width: 80px"/>
          </div>
        </div>
        <small class="text-muted d-block mb-2">{{ $t('campaign.simulator_hint') }}</small>

        <table class="sim-table">
          <tbody>
            <tr>
              <td>{{ $t('campaign.sim_gross') }}</td>
              <td class="text-right">{{ money(sim.gross) }}</td>
            </tr>
            <tr class="text-danger">
              <td>{{ $t('campaign.sim_discount') }}</td>
              <td class="text-right">- {{ money(sim.discount) }}</td>
            </tr>
            <tr>
              <td class="font-medium">{{ $t('campaign.sim_charged') }}</td>
              <td class="text-right font-medium">{{ money(sim.charged) }}</td>
            </tr>
            <tr class="text-danger">
              <td>{{ $t('campaign.sim_commission') }}</td>
              <td class="text-right">- {{ money(sim.commission) }}</td>
            </tr>
            <tr class="text-danger">
              <td>{{ $t('campaign.sim_fee') }}</td>
              <td class="text-right">- {{ money(sim.fee) }}</td>
            </tr>
            <tr :class="sim.net <= 0 ? 'text-danger font-medium' : 'font-medium'">
              <td>{{ $t('campaign.sim_net') }}</td>
              <td class="text-right">{{ money(sim.net) }} ({{ sim.netPct }}%)</td>
            </tr>
          </tbody>
        </table>

        <b-alert v-if="sim.net <= 0" variant="danger" show class="mt-2 mb-0 p-1">
          <small>{{ $t('campaign.sim_negative') }}</small>
        </b-alert>
        <b-alert v-else-if="sim.netPct < 50" variant="warning" show class="mt-2 mb-0 p-1">
          <small>{{ $t('campaign.sim_thin') }}</small>
        </b-alert>
      </div>
    </vx-card>

    <!-- 4. Regras -->
    <vx-card class="mb-4">
      <h4 class="mb-1">{{ $t('campaign.section_rules') }}</h4>
      <div class="vx-row">
        <div class="vx-col sm:w-3/12 w-full">
          <b-form-group :label="$t('campaign.attribution_window')">
            <b-form-input v-model="model.attribution_window_days" type="number" min="1" max="365"/>
            <small class="text-muted">{{ $t('campaign.attribution_window_hint') }}</small>
          </b-form-group>
        </div>
        <div class="vx-col sm:w-3/12 w-full">
          <b-form-group :label="$t('campaign.hold_days')">
            <b-form-input v-model="model.hold_days" type="number" min="0" max="365"/>
          </b-form-group>
        </div>
        <div class="vx-col sm:w-3/12 w-full">
          <b-form-group :label="$t('campaign.hold_from')">
            <b-form-select v-model="model.hold_from" :options="holdFromList"/>
            <small class="text-muted">{{ $t('campaign.hold_from_hint') }}</small>
          </b-form-group>
        </div>
        <div class="vx-col sm:w-3/12 w-full">
          <b-form-group :label="$t('campaign.usage_limit_per_customer')">
            <b-form-input v-model="model.usage_limit_per_customer" type="number" min="1"/>
          </b-form-group>
        </div>
      </div>
      <div class="vx-row">
        <div class="vx-col sm:w-4/12 w-full">
          <b-form-group :label="$t('campaign.first_purchase_only')">
            <b-form-checkbox v-model="model.first_purchase_only" switch/>
            <small class="text-muted">{{ $t('campaign.first_purchase_only_hint') }}</small>
          </b-form-group>
        </div>
        <div class="vx-col sm:w-4/12 w-full">
          <b-form-group :label="$t('campaign.join_mode')">
            <b-form-select v-model="model.join_mode" :options="joinModeList"/>
          </b-form-group>
        </div>
        <div class="vx-col sm:w-4/12 w-full">
          <b-form-group :label="$t('campaign.auto_approve')">
            <b-form-checkbox v-model="model.auto_approve_influencers" switch/>
            <small class="text-muted">{{ $t('campaign.auto_approve_hint') }}</small>
          </b-form-group>
        </div>
      </div>
      <div class="vx-row">
        <div class="vx-col sm:w-4/12 w-full">
          <b-form-group :label="$t('campaign.influencer_free_uses')">
            <b-form-input v-model="model.influencer_free_uses" type="number" min="0" max="99"/>
            <small class="text-muted">{{ $t('campaign.influencer_free_uses_hint') }}</small>
          </b-form-group>
        </div>
      </div>
    </vx-card>

    <!-- 5. Público -->
    <vx-card class="mb-4">
      <h4 class="mb-1">{{ $t('campaign.section_audience') }}</h4>
      <small class="text-muted d-block mb-2">{{ $t('campaign.section_audience_hint') }}</small>

      <b-form-checkbox v-model="audienceAll" switch class="mb-2">
        {{ $t('campaign.audience_all') }}
      </b-form-checkbox>

      <div v-if="!audienceAll" class="vx-row">
        <div class="vx-col sm:w-4/12 w-full">
          <b-form-group :label="$t('campaign.audience_groups')">
            <v-select v-model="audienceGroups" :options="groups" :reduce="o => o.id" label="name" multiple class="w-full"/>
          </b-form-group>
        </div>
        <div class="vx-col sm:w-4/12 w-full">
          <b-form-group :label="$t('campaign.audience_levels')">
            <v-select v-model="audienceLevels" :options="levels" :reduce="o => o.id" label="name" multiple class="w-full"/>
          </b-form-group>
        </div>
        <div class="vx-col sm:w-4/12 w-full">
          <b-form-group :label="$t('campaign.audience_influencers')">
            <v-select v-model="audienceInfluencers" :options="influencers" :reduce="o => o.id" label="name" multiple class="w-full"/>
          </b-form-group>
        </div>
      </div>
    </vx-card>

    <!-- 6. Briefing -->
    <vx-card class="mb-4">
      <h4 class="mb-1">{{ $t('campaign.section_briefing') }}</h4>
      <b-form-group :label="$t('campaign.briefing')">
        <b-form-textarea v-model="model.briefing" rows="3"/>
      </b-form-group>
      <div class="vx-row">
        <div class="vx-col sm:w-6/12 w-full">
          <b-form-group :label="$t('campaign.dos')">
            <b-form-textarea v-model="model.dos" rows="2"/>
          </b-form-group>
        </div>
        <div class="vx-col sm:w-6/12 w-full">
          <b-form-group :label="$t('campaign.donts')">
            <b-form-textarea v-model="model.donts" rows="2"/>
          </b-form-group>
        </div>
      </div>
      <div class="vx-row">
        <div class="vx-col sm:w-4/12 w-full">
          <b-form-group :label="$t('campaign.hashtags')">
            <b-form-input v-model="hashtagsText" type="text" placeholder="#treino, #hubee"/>
          </b-form-group>
        </div>
        <div class="vx-col sm:w-4/12 w-full">
          <b-form-group :label="$t('campaign.mentions')">
            <b-form-input v-model="mentionsText" type="text" placeholder="@clube"/>
          </b-form-group>
        </div>
        <div class="vx-col sm:w-4/12 w-full">
          <b-form-group :label="$t('campaign.disclosure_tag')">
            <b-form-input v-model="model.disclosure_tag" type="text"/>
            <small class="text-muted">{{ $t('campaign.disclosure_tag_hint') }}</small>
          </b-form-group>
        </div>
      </div>
      <b-form-group :label="$t('campaign.rules')">
        <b-form-textarea v-model="model.rules" rows="3"/>
      </b-form-group>
    </vx-card>

    <b-form-row class="justify-end mt-2">
      <b-col cols="auto">
        <b-button variant="primary" @click="save">{{ $t('save') }}</b-button>
      </b-col>
      <b-col v-if="isEdit && model.status === 'DR'" cols="auto">
        <b-button v-permission="'campaigns.edit'" variant="success" @click="publish">
          {{ $t('campaign.publish') }}
        </b-button>
      </b-col>
      <b-col cols="auto">
        <b-button variant="outline-secondary" @click="goBack">
          <feather-icon icon="CornerUpLeftIcon" class="mr-50"/>
          <span class="align-middle">{{ $t('back') }}</span>
        </b-button>
      </b-col>
    </b-form-row>
  </div>
</template>

<script>
import {
  BFormGroup, BFormInput, BFormTextarea, BFormSelect, BFormRadioGroup,
  BFormCheckbox, BButton, BFormRow, BCol, BInputGroup, BAlert
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import CampaignService from '@/services/api/CampaignService'
import ProductService from '@/services/api/ProductService'
import LegalEntityService from '@/services/api/LegalEntityService'
import MyFeesService from '@/services/api/MyFeesService'
import InfluencerService from '@/services/api/InfluencerService'
import InfluencerGroupService from '@/services/api/InfluencerGroupService'
import InfluencerLevelService from '@/services/api/InfluencerLevelService'

export default {
  components: {
    BFormGroup, BFormInput, BFormTextarea, BFormSelect, BFormRadioGroup,
    BFormCheckbox, BButton, BFormRow, BCol, BInputGroup, BAlert, vSelect
  },
  props: { id: { type: [Number, String], default: null } },
  data: () => ({
    service: null,
    products: [], legalEntities: [], influencers: [], groups: [], levels: [],
    audienceAll: true,
    audienceGroups: [], audienceLevels: [], audienceInfluencers: [],
    hashtagsText: '', mentionsText: '',
    // A taxa da Hubee agora tem endpoint (POST /financial/fees/simulate, mesmo
    // motor da cobrança): escolhido o clube, o campo é preenchido com a taxa
    // efetiva real dele. Continua editável para cenários hipotéticos.
    simPrice: 100,
    simFeePct: 10,
    model: {
      id: null, name: '', legal_entity_id: null, starts_at: null, ends_at: null,
      objective: '', product_ids: [],
      discount_type: 'PE', discount_amount: 10,
      commission_type: 'PE', commission_amount: 10, commission_base: 'LQ',
      cost_bearer: 'ES', budget_cap: null,
      attribution_window_days: 30, hold_days: 30, hold_from: 'DL',
      usage_limit_per_customer: 1, first_purchase_only: false,
      join_mode: 'MX', auto_approve_influencers: false, influencer_free_uses: null,
      briefing: '', dos: '', donts: '', rules: '', disclosure_tag: '#publi',
      status: 'DR'
    },
    typeList: [{ text: '%', value: 'PE' }, { text: 'R$', value: 'VA' }],
    baseList: [{ text: 'Líquido', value: 'LQ' }, { text: 'Bruto', value: 'GR' }],
    bearerList: [
      { text: 'Estabelecimento', value: 'ES' },
      { text: 'Hubee', value: 'HU' },
      { text: 'Dividido', value: 'SH' }
    ],
    holdFromList: [
      { text: 'Data de realização', value: 'DL' },
      { text: 'Data do pagamento', value: 'PY' }
    ],
    joinModeList: [
      { text: 'Convite e catálogo', value: 'MX' },
      { text: 'Somente convite', value: 'IN' },
      { text: 'Somente catálogo', value: 'OP' }
    ]
  }),
  computed: {
    isEdit() { return !_.isNil(this.id) },
    /**
     * Espelha a conta do backend: desconto sai do preço, comissão incide sobre a
     * base escolhida, e a taxa da plataforma sobre o que foi efetivamente cobrado.
     */
    sim() {
      const gross = Number(this.simPrice || 0)
      const dAmt = Number(this.model.discount_amount || 0)
      const discount = Math.min(gross, this.model.discount_type === 'PE' ? gross * (dAmt / 100) : dAmt)
      const charged = Math.max(0, gross - discount)

      const base = this.model.commission_base === 'GR' ? gross : charged
      const cAmt = Number(this.model.commission_amount || 0)
      const commission = Math.min(base, this.model.commission_type === 'PE' ? base * (cAmt / 100) : cAmt)

      const fee = charged * (Number(this.simFeePct || 0) / 100)
      const net = charged - commission - fee

      return {
        gross, discount, charged, commission, fee, net,
        netPct: charged > 0 ? Math.round((net / charged) * 100) : 0
      }
    }
  },
  watch: {
    'model.legal_entity_id'(legalEntityId) {
      this.loadHubeeFee(legalEntityId)
    }
  },
  methods: {
    money(v) {
      return Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    },
    loadHubeeFee(legalEntityId) {
      if (!legalEntityId) return
      MyFeesService.build(this.$vs)
        .simulate({ amount_cents: 10000, legal_entity_id: legalEntityId })
        .then(result => {
          if (result && result.has_plan && result.amount_cents > 0) {
            this.simFeePct = Math.round((result.fee_amount_cents / result.amount_cents) * 10000) / 100
          }
        })
        .catch(() => {})
    },
    loadAux() {
      ProductService.build(this.$vs).all().then(l => { this.products = l || [] }, () => {})
      LegalEntityService.build(this.$vs).all().then(l => { this.legalEntities = l || [] }, () => {})
      InfluencerService.build(this.$vs).all().then(l => { this.influencers = l || [] }, () => {})
      InfluencerGroupService.build(this.$vs).all().then(l => { this.groups = l || [] }, () => {})
      InfluencerLevelService.build(this.$vs).all().then(l => { this.levels = l || [] }, () => {})
    },
    loadData(id) {
      this.$vs.loading()
      this.service.detail(id).then(
        r => {
          Object.keys(this.model).forEach(k => { if (r[k] !== undefined) this.model[k] = r[k] })
          this.model.starts_at = r.starts_at ? String(r.starts_at).substring(0, 10) : null
          this.model.ends_at = r.ends_at ? String(r.ends_at).substring(0, 10) : null
          this.model.budget_cap = r.budget_cap_cents ? Number(r.budget_cap_cents) / 100 : null
          this.model.product_ids = (r.campaign_products || []).map(p => p.product_id)
          this.hashtagsText = (r.hashtags || []).join(', ')
          this.mentionsText = (r.mentions || []).join(', ')

          const audiences = r.audiences || []
          this.audienceAll = audiences.some(a => a.target_type === 'AL')
          this.audienceGroups = audiences.filter(a => a.target_type === 'GR').map(a => a.target_id)
          this.audienceLevels = audiences.filter(a => a.target_type === 'LV').map(a => a.target_id)
          this.audienceInfluencers = audiences.filter(a => a.target_type === 'IF').map(a => a.target_id)
        },
        () => { this.$vs.loading.close() }
      ).finally(() => { this.$vs.loading.close() })
    },
    buildAudiences() {
      if (this.audienceAll) return [{ target_type: 'AL' }]
      return [
        ...this.audienceGroups.map(id => ({ target_type: 'GR', target_id: id })),
        ...this.audienceLevels.map(id => ({ target_type: 'LV', target_id: id })),
        ...this.audienceInfluencers.map(id => ({ target_type: 'IF', target_id: id }))
      ]
    },
    csvToArray(text) {
      return String(text || '').split(',').map(s => s.trim()).filter(Boolean)
    },
    save() {
      this.$vs.loading()
      this.service.createOrUpdate({
        id: this.id,
        name: this.model.name,
        legal_entity_id: this.model.legal_entity_id,
        starts_at: this.model.starts_at || null,
        ends_at: this.model.ends_at || null,
        objective: this.model.objective,
        discount_type: this.model.discount_type,
        discount_amount: this.model.discount_amount,
        commission_type: this.model.commission_type,
        commission_amount: this.model.commission_amount,
        commission_base: this.model.commission_base,
        cost_bearer: this.model.cost_bearer,
        budget_cap_cents: this.model.budget_cap ? Math.round(Number(this.model.budget_cap) * 100) : null,
        attribution_window_days: this.model.attribution_window_days,
        hold_days: this.model.hold_days,
        hold_from: this.model.hold_from,
        usage_limit_per_customer: this.model.usage_limit_per_customer,
        first_purchase_only: this.model.first_purchase_only,
        join_mode: this.model.join_mode,
        auto_approve_influencers: this.model.auto_approve_influencers,
        // Vazio é desligado: mandar '' viraria 0 no banco e o influencer perderia
        // a distinção entre "sem cortesia" e "cortesia zerada".
        influencer_free_uses: this.model.influencer_free_uses === '' || this.model.influencer_free_uses === null
          ? null
          : Number(this.model.influencer_free_uses),
        briefing: this.model.briefing,
        dos: this.model.dos,
        donts: this.model.donts,
        rules: this.model.rules,
        disclosure_tag: this.model.disclosure_tag,
        hashtags: this.csvToArray(this.hashtagsText),
        mentions: this.csvToArray(this.mentionsText)
      }).then(
        data => {
          const id = this.id || data.id
          // Produtos e público são coleções e têm endpoints próprios; sem eles a
          // campanha não passa na validação de publicação.
          return Promise.all([
            this.service.saveProducts(id, this.model.product_ids.map(pid => ({ product_id: pid }))),
            this.service.saveAudiences(id, this.buildAudiences())
          ]).then(() => {
            this.notifySuccess(this.$vs, this.$t('messages.success'))
            if (!this.isEdit) this.$router.push(`/campaigns/${id}/edit`)
          })
        },
        error => this.showErrors(this.$validator, error, this)
      ).finally(() => { this.$vs.loading.close() })
    },
    publish() {
      this.$vs.loading()
      this.service.publish(this.id).then(
        c => {
          this.model.status = c.status
          this.notifySuccess(this.$vs, this.$t('messages.success'))
        },
        error => this.showErrors(this.$validator, error, this)
      ).finally(() => { this.$vs.loading.close() })
    },
    goBack() { this.$router.push('/campaigns') }
  },
  beforeMount() { this.service = CampaignService.build(this.$vs) },
  mounted() {
    this.loadAux()
    if (this.id) this.loadData(this.id)
  }
}
</script>

<style scoped>
.simulator {
  border: 1px dashed rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  padding: 0.85rem;
  margin-top: 0.5rem;
}

.sim-table {
  width: 100%;
  max-width: 420px;
  font-size: 0.875rem;
}

.sim-table td {
  padding: 0.15rem 0;
}

.text-right {
  text-align: right;
}
</style>
